"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var widgets_1 = require("@shared/constants/widgets");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
var PAGE_BREAK_WIDGET_TYPE = widgets_1.PR_WIDGETS.PAGE_BREAK;
/**
 * Contains core logic for the layout engine.
 * Responsible for updating widgets and their layouts and
 * determining the pages that widgets should be on.
 *
 * Do not inject prPagesManager.
 * Keep layout engine logic and page update logic separate.
 */
function LayoutEngineUtilsFactory(autoHeight, pageLayout, chunker, widgetUtils, pagesReader) {
    return {
        getWidgetPage: getWidgetPage,
        getWidgetInsertIndex: getWidgetInsertIndex,
        positionWidget: positionWidget,
        ensureWidgetLayout: ensureWidgetLayout,
        updateWidgetScale: updateWidgetScale,
        shouldUpdateChunks: shouldUpdateChunks,
        updateWidgetChunks: updateWidgetChunks
    };
    /************************************
     *          PUBLIC METHODS          *
     ************************************/
    /*
     * Returns the index of the page that the widget should be on
     */
    function getWidgetPage(widget) {
        var layout = widget.layout;
        var rows = pageLayout.getRows();
        var height;
        if (widget._type === PAGE_BREAK_WIDGET_TYPE) {
            // always push the page break onto the next page
            height = rows;
        }
        else {
            height = Math.max(Math.min(layout.rowSpan || 1, rows), 1);
        }
        /**
         * Can't be math.floor because we want a full-height widget take up the first page.
         * `layout.row + height` = number of rows down the report,
         * `row` = number of rows of a single page.
         * So `(layout.row + height) / rows` = page that the widget should be on
         */
        return Math.ceil((layout.row + height) / rows) - 1 || 0;
    }
    /**
     * Gets the index of the widgets array that
     * the widgets should be inserted at.
     *
     * 	(1) Gets the last index in `widgetList` that is before the `widget`
     * 	(2) Returns either that index or the next index
     * 		depending on where `widget` collides with the widget at the index.
     */
    function getWidgetInsertIndex(widget, widgetList, after) {
        if (!widget || !widgetList || !widgetList.length) {
            return widgetList.length;
        }
        var idx = getInsertIndex(widgetList, widget);
        var widgetPageIndex = getWidgetPage(widget);
        var breakWidget = pagesReader.getPageBreakWidget(widgetPageIndex);
        var breakWidgetIdx = breakWidget && widgetList.indexOf(breakWidget) || -1;
        // if we have a break widget, use that index
        if (breakWidgetIdx >= idx) {
            return breakWidgetIdx + 1;
        }
        var collided = widgetList[idx];
        // It must be placed before the item it collides with if it is above the
        // middle of that widget. It must be placed after the item it collides with
        // if it is below the middle of the collided widget
        if (collided) {
            /**
             * @todo extract this logic to a function, e.g. isBelow()
             */
            var wl = widget.layout || {};
            var cl = collided.layout || {};
            var wt = wl.row || 0; // the top of our moving widget
            var wb = wt + (wl.rowSpan || 0); // the bottom of our moving widget
            var ct = cl.row || 0; // the top of the colided widget
            var cb = ct + (cl.rowSpan || 0); // the bottom of the colided widget
            var cm = ct + (cl.rowSpan || 0) / 2; // the middle of the colided widget
            // let's insert after
            if (wb > cm && after || wb > cb || !after && wt > cm) {
                idx++;
            }
        }
        return idx;
    }
    /*
     * Moves the widget to the best empty spot in a list of widgets
     * Makes sure the widget ends up on the correct page
     */
    function positionWidget(widgets, widget) {
        if (!widget || !widgets) {
            return;
        }
        var idx = widgets.indexOf(widget);
        var toCheck = idx < 0 ? widgets : widgets.slice(0, idx);
        var prev = toCheck[toCheck.length - 1] || {};
        // if the widget is moving, we allow it to go above the previous one
        var prevRow = !widget.moving && prev.layout && prev.layout.row || 0;
        var layout = widget.layout;
        var pageBreak = lodash_1.default.findLast(toCheck, { _type: PAGE_BREAK_WIDGET_TYPE });
        var lowestRow = ((pageBreak || {}).layout || {}).row || 0;
        var pageBreakIdx = pageBreak ? widgets.indexOf(pageBreak) : -1;
        if (widget._type !== PAGE_BREAK_WIDGET_TYPE || pageBreakIdx === -1 || pageBreakIdx !== idx - 1) {
            // move the widget up till it hits zero
            layout.row = Math.max(layout.row || 0, prevRow, lowestRow);
            while (layout.row > lowestRow && !getCollidedWidget(toCheck, widget)) {
                layout.row--;
            }
        }
        // push the widget down till it doesn't overlap
        var len = toCheck.length;
        var collided = getCollidedWidget(toCheck, widget);
        while (len-- && collided) {
            var collidedLayout = collided.layout;
            layout.row = collidedLayout.row + collidedLayout.rowSpan;
            collided = getCollidedWidget(toCheck, widget);
        }
        if (!widget.isChunked) {
            // the widget may need to be moved to the next page
            var pageIdx = getWidgetPage(widget);
            layout.row = Math.max(layout.row, pageIdx * pageLayout.getRows());
        }
    }
    function ensureWidgetLayout(widget) {
        if (widget._type === PAGE_BREAK_WIDGET_TYPE) {
            return ensurePageBreakLayout(widget);
        }
        var props = {
            row: 0,
            col: 0,
            rowSpan: 1,
            colSpan: 1,
            page: 0
        };
        var layout = widget.layout || props;
        lodash_1.default.each(props, function (val, prop) {
            layout[prop] = Math.max(layout[prop] || val, val);
        });
        layout.autoHeightType = autoHeight.autoHeightType(widget._type);
        // we add our ID here because we want a unique identifier for widget chunks
        layout.id = widget._id;
        widget.layout = layout;
        widget.isChunked = !!layout.autoHeightType && !widgetUtils.isFixedHeightWidget(widget._id);
        // just in case this was left over
        delete widget.chunks;
        delete layout.chunkIndex;
        updateWidgetScale(widget);
    }
    // these are used to transform reports if the page size orientation or cellSize had changed
    function updateWidgetScale(widget) {
        var rows = pageLayout.getRows();
        var cols = pageLayout.getCols();
        var layout = widget.layout || {};
        // ensure widget does not span greater than page size
        layout.colSpan = Math.min(cols, layout.colSpan);
        if (!widget.isChunked) {
            layout.rowSpan = Math.min(rows, layout.rowSpan);
        }
        // make sure page size exists before we start to use it
        layout.pageDims = layout.pageDims || rows + ',' + cols;
        var pageDims = layout.pageDims.split(',');
        var pageSize = {
            /* tslint:disable */
            rows: parseInt(pageDims[0]),
            cols: parseInt(pageDims[1])
            /* tslint:enable */
        };
        if (pageSize.rows === rows && pageSize.cols === cols) {
            return;
        }
        var pctCol = pageSize.cols / cols;
        var pctRow = pageSize.rows / rows;
        layout.col = Math.floor(layout.col / pctCol);
        layout.colSpan = Math.max(Math.floor(layout.colSpan / pctCol), 1);
        layout.row = Math.floor(layout.row / pctRow);
        layout.rowSpan = Math.max(Math.floor(layout.rowSpan / pctRow), 1);
        // this has to be a string so our watchCollection can monitor it for changes in widget.js
        layout.pageDims = rows + ',' + cols;
        // in case this was already there
        delete layout.pageSize;
        // return true if the widget is chunked
        return !!widget.chunks;
    }
    function shouldUpdateChunks(widget) {
        var chunks = widget.chunks || [];
        var chunkLen = chunks.length;
        if (!chunkLen) {
            return true;
        }
        var wLayout = widget.layout;
        var pageRows = pageLayout.getRows();
        var neededChunks = Math.ceil((wLayout.row + wLayout.rowSpan) / pageRows) - Math.floor(wLayout.row / pageRows);
        var firstLayout = chunks[0].layout;
        var lastLayout = chunks[chunkLen - 1].layout;
        return (chunkLen !== neededChunks ||
            wLayout.row !== firstLayout.row ||
            wLayout.col !== firstLayout.col ||
            lastLayout.col !== firstLayout.col ||
            wLayout.colSpan !== firstLayout.colSpan ||
            lastLayout.colSpan !== firstLayout.colSpan ||
            wLayout.rowSpan !== (lastLayout.row + lastLayout.rowSpan) - firstLayout.row);
    }
    function updateWidgetChunks(widget) {
        if (!widget.chunks) {
            widget.chunks = [];
        }
        // widget chunks changed! We need to update BAD
        chunker.updateChunks(widget);
        var layout = widget.layout;
        var chunks = widget.chunks;
        var pageRows = pageLayout.getRows();
        var startRow = layout.row || 0;
        var startPage = Math.max(Math.floor(startRow / pageRows), 0);
        layout.rowSpan = Math.max(layout.rowSpan || 0, 1);
        // loop through row by row, and determine which page the chunk would be on
        var curChunk;
        for (var row = startRow, endRow = startRow + layout.rowSpan; row < endRow; row++) {
            var chunkPage = Math.max(Math.floor(row / pageRows), 0);
            var chunkIndex = Math.max(chunkPage - startPage, 0);
            if (chunkIndex !== curChunk) {
                curChunk = chunkIndex;
                initChunk(widget, chunkIndex, row, 0);
            }
            // update the rowspan
            chunks[curChunk].layout.rowSpan++;
        }
        // remove and return any extra chunks
        return chunks.splice(curChunk + 1);
    }
    /************************************
     *          PRIVATE HELPERS         *
     ************************************/
    /**
     * Returns the last index in the `widgets` array
     * that is before the `widget` (assuming `widgets` is sorted).
     * If no widgets are before the `widget`, return -1.
     */
    function getInsertIndex(widgets, widget) {
        var idx = -1;
        while (widgets[idx + 1] && compareWidgets(widget, widgets[idx + 1]) >= 0) {
            idx++;
        }
        return idx;
    }
    /*
     * This comparision is the basis for the entire layout engine
     * It Widgets with a higher row and column are greater.
     * It first compares by row, then if the rows are the same, it compares by column.
     */
    function compareWidgets(a, b) {
        var al = a.layout;
        var bl = b.layout;
        if (al.row > bl.row) {
            return 1;
        }
        if (al.row < bl.row) {
            return -1;
        }
        if (al.col > bl.col) {
            return 1;
        }
        if (al.col < bl.col) {
            return -1;
        }
        if (b._type === PAGE_BREAK_WIDGET_TYPE) {
            return 1;
        }
        if (a._type === PAGE_BREAK_WIDGET_TYPE) {
            return -1;
        }
        return 0;
    }
    /*
     * Returns true if a widget.layout collides with another widget.layout
     */
    function collides(a, b) {
        return !(
        // Page breaks don't have rowspan, so we need a small one on "a" to make sure they don't ever overlap
        // We only add it to "a" so moving a widget works properly when they are right below a page-break
        a.row + (a.rowSpan || 0.1) <= b.row ||
            a.row >= b.row + b.rowSpan ||
            a.col + a.colSpan <= b.col ||
            a.col >= b.col + b.colSpan);
    }
    function ensurePageBreakLayout(widget) {
        widget.layout = angular_1.extend(widget.layout || { row: 0 }, {
            rowSpan: 0,
            col: 0,
            colSpan: pageLayout.getCols()
        });
    }
    // TODO: figure out how moving a widget from one page to the next will trigger a collision
    /*
     * Returns the first widget in the area specified by dims
     * note: dims imitates a widget.layout
     */
    function getCollidedWidget(widgets, pivotWidget) {
        if (!widgets || !widgets.length) {
            return;
        }
        var dims = pivotWidget.layout;
        return lodash_1.default.find(widgets, function (widget) {
            return widget && widget !== pivotWidget && collides(dims, widget.layout);
        });
    }
    function initChunk(widget, chunkIndex, row, rowSpan) {
        var chunks = widget.chunks;
        var layout = widget.layout;
        // make sure the chunk exists
        while (chunks.length <= chunkIndex) {
            chunks.push({
                _id: widget._id,
                _type: widget._type,
                layout: {
                    id: widget._id + '.' + chunks.length,
                    chunkIndex: chunks.length
                }
            });
        }
        // update the chuck layout's width row, and rowspan
        angular_1.extend(chunks[chunkIndex].layout, {
            chunkIndex: chunkIndex,
            row: row,
            rowSpan: rowSpan,
            col: layout.col,
            colSpan: layout.colSpan,
            autoHeightType: layout.autoHeightType
        });
    }
}
(function (angularModule) {
    angularModule.factory('pr.layoutEngineUtils', [
        'prAutoHeightHelper',
        'prPageLayoutService',
        'prWidgetChunkerFactory',
        'pr.widgetUtils',
        'pr.uiPagesManager',
        LayoutEngineUtilsFactory
    ]);
})(angular_1.module('qualtrics.pr'));
